import { memo, type ReactNode, useRef, useState } from "react";
import { Link } from "~/common/components/Link";
import {
  type CertifiedCompetencesType,
  type GoogleMarker,
  type ProductCompetencesType,
} from "~/domains/salesPartner/types/SalesPartnerTypes";
import { containsFeature, SalesPartnerFeature } from "~/domains/salesPartner/utils/transformers";
import { Logos } from "~/common/components/Logos";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import Modal, { ModalSize } from "~/common/components/Modal";
import { openModal } from "~/common/utils/ModalService";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { Box, Button, Stack, styled, type SxProps, type Theme, Typography } from "~/common/mui";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";

import Spinner from "~/common/components/Spinner/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { clearSavedAffiliateThunk, saveAffiliateThunk } from "~/domains/affiliate/redux/actions";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import {
  SalesPartnerActions,
  SalesPartnerMethods,
  type SalesPartnerSearchFormValues,
  trackPartnerFinderSearch,
  trackSalesPartnerActions,
} from "~/app/analytics/ga/salesPartner";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import { Alert } from "~/common/mui/Alert/Alert";
import RichTooltip, { type RichTooltipHandle } from "~/common/components/RichTooltip/RichTooltip";
import Tag from "~/common/components/Tag/Tag";
import { salesPartnerMobileDetailsModalSx } from "~/features/salesPartner/components/salesPartnerModalStyles";
import { useAffiliateExpirationTimeHours } from "~/app/config/useAppConfig";

interface Props {
  company: GoogleMarker;
  activeTab: string;
  className?: string;
  action?: ReactNode;
  isCheckout?: boolean;
  openRegisterSnackbar?: () => void;
  searchValues: SalesPartnerSearchFormValues;
}

interface PreferredSalesPartnerCheckboxProps {
  checked: boolean;
  className?: string;
  label: ReactNode;
  onCheck: (checked: boolean) => void;
  sx?: SxProps<Theme>;
}

export const PreferredSalesPartnerCheckbox = ({
  checked,
  className,
  label,
  onCheck,
  sx,
}: PreferredSalesPartnerCheckboxProps) => (
  <Box className={className} marginBottom={2} sx={sx}>
    <CheckboxWithLabel checked={checked} onCheck={onCheck} sx={{ mt: { phone: 0, tablet: -1 } }}>
      {label}
    </CheckboxWithLabel>
  </Box>
);

const CompetencesIcon = styled("img")({
  width: 16,
  height: 16,
});

const CompetencesList = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

export const Competences = (
  certifiedCompetences: CertifiedCompetencesType[] = [],
  productCompetences: ProductCompetencesType[] = [],
) => {
  const { t } = useTranslation();
  const tooltipRef = useRef<RichTooltipHandle>(null);

  const handleClose = () => {
    tooltipRef.current?.close();
  };
  return (
    <>
      {productCompetences.length > 0 && (
        <Stack gap={1}>
          <Typography>{t("partnerFinder.search.productFilter")}</Typography>
          <CompetencesList>
            {productCompetences.map(({ key, label, iconUrl, hoverText }) => (
              <RichTooltip
                key={key}
                content={hoverText && t(hoverText)}
                disableHover={true}
                actions={[
                  <Button variant="text" onClick={handleClose}>
                    {t("close")}
                  </Button>,
                ]}
                ref={tooltipRef}
              >
                <Tag icon={iconUrl ? <CompetencesIcon src={iconUrl} /> : undefined} iconPosition="start">
                  {t(label)}
                </Tag>
              </RichTooltip>
            ))}
          </CompetencesList>
        </Stack>
      )}

      {certifiedCompetences.length > 0 && (
        <Stack gap={1}>
          <Typography>{t("partnerFinder.search.serviceFilter")}</Typography>
          <CompetencesList>
            {certifiedCompetences.map(({ key, label, iconUrl, hoverText }) => (
              <RichTooltip
                key={key}
                content={hoverText && t(hoverText)}
                disableHover={true}
                actions={[
                  <Button variant="text" onClick={handleClose}>
                    {t("close")}
                  </Button>,
                ]}
                ref={tooltipRef}
              >
                <Tag icon={iconUrl ? <CompetencesIcon src={iconUrl} /> : undefined} iconPosition="start">
                  {t(label)}
                </Tag>
              </RichTooltip>
            ))}
          </CompetencesList>
        </Stack>
      )}
    </>
  );
};

const ButtonsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    justifyContent: "end",
  },
}));

const CertificationsWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

export const SPCompanyDetails = memo(
  ({ company, activeTab, action, isCheckout, className, openRegisterSnackbar, searchValues }: Props) => {
    const hasINA = containsFeature(company.features, SalesPartnerFeature.INA);
    const hasFAG = containsFeature(company.features, SalesPartnerFeature.FAG);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const isRegistered = userIsRegistered();
    const expirationTimeHours = useAffiliateExpirationTimeHours();
    const savedSalesPartner = useSelector(selectSavedAffiliate);
    const affiliateId = savedSalesPartner?.affiliateId;
    const isPreferred = !!affiliateId && company?.affiliateId === affiliateId;
    const hasVisitedAffiliate = !!useSelector(selectVisitedAffiliate);

    const isTablet = useIsMinTabletSize();
    const isDesktop = useIsMinDesktopSize();

    const [isChecked, setChecked] = useState(isPreferred);
    const [isSaved, setSaved] = useState<"loading" | "saved" | "hide">("hide");

    const savePartnerAction = async () => {
      if (!isRegistered && openRegisterSnackbar) {
        openRegisterSnackbar();
      }
      // Optimistic update
      setChecked(true);

      if (isRegistered) {
        setSaved("loading");
      }
      const success = await dispatch(
        saveAffiliateThunk({
          affiliateId: company.affiliateId,
          subsidiaries: [],
          logo: "",
          name: company.address.companyNameDisplay,
          salesPartnerId: company.subsidiaryId,
          expirationTimeHours,
        }),
      );
      setSaved("saved");
      if (success) {
        trackSalesPartnerActions(
          SalesPartnerActions.ADD_PREFERRED,
          activeTab === "map" ? SalesPartnerMethods.MAP : SalesPartnerMethods.DETAILS,
          transformSelectedMarkerData(t, company, savedSalesPartner),
        );
      } else {
        setChecked(false);
      }
    };

    const deletePartnerAction = async () => {
      setChecked(false);
      setSaved("hide");
      const success = await dispatch(clearSavedAffiliateThunk());
      if (success) {
        trackSalesPartnerActions(
          SalesPartnerActions.REMOVE_PREFERRED,
          activeTab === "map" ? SalesPartnerMethods.MAP : SalesPartnerMethods.DETAILS,
          transformSelectedMarkerData(t, company, savedSalesPartner),
        );
      }
    };

    const changeSalesPartnerModal = (close: () => void) => {
      const oldSalesPartner = savedSalesPartner?.name;
      const newSalesPartner = company.address.companyNameDisplay;
      return (
        <Modal
          size={ModalSize.SMALL}
          modalClassName={classNames(!isTablet && "scha__modal__full-screen-view scha__sp__mobile-details")}
          modalPaperSx={salesPartnerMobileDetailsModalSx}
          onClose={close}
          title={t("affiliateSalesPartner.overrideModal.title")}
          footerChildren={
            <ButtonsRow>
              <Button onClick={close} variant="text">
                {t("affiliateSalesPartner.overrideModal.no")}
              </Button>
              <Button
                onClick={() => {
                  savePartnerAction();
                  close();
                }}
              >
                {t("affiliateSalesPartner.overrideModal.yes")}
              </Button>
            </ButtonsRow>
          }
        >
          <p>{t("affiliateSalesPartner.overrideModal.subtitle", { old: oldSalesPartner, new: newSalesPartner })}</p>
        </Modal>
      );
    };

    const onCheckboxToggle = (val: boolean) => {
      if (val) {
        if (company.affiliateId !== affiliateId && affiliateId) {
          openModal("change-sales-partner-modal", changeSalesPartnerModal);
        } else {
          savePartnerAction();
        }
      } else {
        deletePartnerAction();
      }
    };

    const IsSavedMessage = () => {
      if (isSaved === "loading") {
        return <Spinner />;
      }
      if (isSaved === "saved") {
        return (
          <Alert className="mt-3" severity="info">
            {t("salesPartner.gmap.card.saved")}
          </Alert>
        );
      }
      return <></>;
    };

    const CertificationModal = (close: () => void) => (
      <Modal
        hideClose
        size={ModalSize.SMALL}
        onClose={close}
        title={t("partnerFinder.details.certified.by")}
        footerChildren={
          <Button variant={"text"} onClick={close}>
            {t("close")}
          </Button>
        }
      >
        <ul>
          {company.certifiedDescriptionList.map((description) => (
            <li key={description}>{t(description)}</li>
          ))}
        </ul>
      </Modal>
    );

    const hasCertifications = company.certifiedCompetences.length > 0;
    const isSingleCertification = company.certifiedCompetences.length === 1;

    const certifications = hasCertifications && (
      <CertificationsWrapper>
        {isSingleCertification ? (
          <Typography>{t(company.certifiedDescriptionList[0])}</Typography>
        ) : (
          <>
            <Typography>{`${company.address.companyNameDisplay} ${t(
              "partnerFinder.details.certification.info",
            )}`}</Typography>
            <Button
              variant="text"
              onClick={() => {
                trackPartnerFinderSearch(
                  searchValues,
                  "View Certifications Intended",
                  "Click",
                  transformSelectedMarkerData(t, company),
                );
                openModal("certifications-modal", CertificationModal);
              }}
            >
              {t("partnerFinder.details.viewCertifications")}
            </Button>
          </>
        )}
      </CertificationsWrapper>
    );

    return (
      <Box
        className={classNames("scha__sp__company-details", className)}
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isCheckout && { flex: 1 }),
        }}
      >
        {!hasVisitedAffiliate && (
          <PreferredSalesPartnerCheckbox
            checked={isChecked}
            label={t("affiliateSalesPartner.banner.text.save")}
            onCheck={onCheckboxToggle}
          />
        )}
        <Stack gap={2}>
          <Stack gap={3}>
            <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
              <Stack gap={2} flex={1}>
                {company.logoUrl && <CompanyLogo src={company.logoUrl} alt="Comapny logo" />}
                <Stack gap={1}>
                  <Typography variant="bodyLarge">{company.displayName}</Typography>
                  <AddressAndContact gap={3}>
                    <Box flex={1}>
                      <Stack>
                        <Typography variant="bodyMedium">
                          {company.address.line1}
                          <br />
                          {company.address.line2 && (
                            <>
                              {company.address.line2}
                              <br />
                            </>
                          )}
                          {t(company.address.country.name)}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box flex={1}>
                      <Stack gap={0.5}>
                        {company.address.phone && (
                          <Stack direction={"row"} gap={1}>
                            <Typography color="textSecondary">{t("salesPartner.contact")}:</Typography>
                            <Typography>{company.address.phone}</Typography>
                          </Stack>
                        )}
                        {company.address.email && (
                          <Stack direction={"row"} gap={1}>
                            <Typography color="textSecondary">{t("salesPartner.mail")}:</Typography>
                            <Link href={`mailto:${company.address.email}`}>{company.address.email}</Link>
                          </Stack>
                        )}
                        {company.address.fax && (
                          <Stack direction={"row"} gap={1}>
                            <Typography color="textSecondary">{t("salesPartner.fax")}:</Typography>
                            <Typography>{company.address.fax}</Typography>
                          </Stack>
                        )}
                        {company.address.website && (
                          <Stack direction={"row"} gap={1}>
                            <Typography color="textSecondary">{t("salesPartner.website")}:</Typography>
                            <Link href={company.address.website} skipResolve={true} target="_blank">
                              {company.address.website}
                            </Link>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </AddressAndContact>
                </Stack>
              </Stack>
              <CompanyLogos $center={isCheckout && isDesktop}>
                {hasINA && <Logos.INA />}
                {hasFAG && <Logos.FAG />}
              </CompanyLogos>
            </Stack>

            {action}
            {!isCheckout && Competences(company.certifiedCompetences, company.productCompetences)}
            {certifications}
          </Stack>
        </Stack>
        <IsSavedMessage />
      </Box>
    );
  },
);

const AddressAndContact = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.up("desktop-m")]: {
    flexDirection: "row",
  },
}));

const CompanyLogo = styled("img")(({ theme }) => ({
  width: 123,
  height: 40,
  objectFit: "contain",
  marginBottom: theme.spacing(1),
}));

const CompanyLogos = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$center",
})<{ $center?: boolean }>(({ theme, $center }) => ({
  width: 32,
  flex: "none",
  justifyContent: $center ? "center" : undefined,
  "& > * + *": {
    marginTop: theme.spacing(2),
  },
}));
